// src/components/profile/SignatureUpload.jsx
import React, { useState, useRef, useEffect } from 'react';
import { FileSignature, Upload, Trash2, Edit2, Save, X } from 'lucide-react';
import { addSignature } from '../../../../services/Queries/draftSigns';
import { toast } from 'react-toastify';
import { useAuths } from '../../../../context/AuthProviders';

const SignatureUpload = ({ currentSignature, onSignatureChange }) => {
  const { user, loading: authLoading } = useAuths();
  
  const [signaturePreview, setSignaturePreview] = useState(currentSignature);
  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [signatureText, setSignatureText] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [canvasData, setCanvasData] = useState(null);
  
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('File size must be less than 2MB');
        return;
      }

      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a valid image file (PNG, JPG, SVG)');
        return;
      }

      setIsUploading(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
        onSignatureChange?.(reader.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrawSignature = () => {
    setIsEditing(true);
    setTimeout(() => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }, 100);
  };

  const handleMouseDown = (e) => {
    if (!isEditing) return;
    setDrawing(true);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const handleMouseMove = (e) => {
    if (!drawing || !isEditing) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const handleMouseUp = () => {
    setDrawing(false);
  };

  const saveCanvasSignature = async () => {
    if (!user?.id) {
      toast.error('User not found. Please log in again.');
      return;
    }

    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    
    setIsUploading(true);
    try {
      const user_id = user.id;
      const user_role = 'hr'; // or get from user.role if available

      const result = await addSignature(user_id, user_role, dataUrl);
      
      setSignaturePreview(dataUrl);
      onSignatureChange?.(dataUrl);
      setCanvasData(dataUrl);
      
      console.log('Signature saved:', result);
      toast.success('Signature uploaded successfully!');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to upload signature: ' + error.message);
    } finally {
      setIsUploading(false);
      setIsEditing(false);
    }
  };

  const handleClearSignature = async () => {
    setSignaturePreview(null);
    setCanvasData(null);
    onSignatureChange?.(null);
    toast.info('Signature cleared');
  };

  const handleTextSignature = () => {
    alert('Coming Soon...')
          toast.info('Coming Soon....')
    // const canvas = document.createElement('canvas');
    // canvas.width = 400;
    // canvas.height = 150;
    // const ctx = canvas.getContext('2d');
    
    // // Background
    // ctx.fillStyle = '#ffffff';
    // ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // // Signature text
    // ctx.fillStyle = '#000000';
    // ctx.font = 'italic 36px Brush Script MT, cursive';
    // ctx.textAlign = 'center';
    // ctx.fillText(signatureText, canvas.width / 2, canvas.height / 2 + 10);
    
    // // Underline
    // const textWidth = ctx.measureText(signatureText).width;
    // ctx.beginPath();
    // ctx.moveTo((canvas.width - textWidth) / 2, canvas.height / 2 + 20);
    // ctx.lineTo((canvas.width + textWidth) / 2, canvas.height / 2 + 20);
    // ctx.stroke();
    
    // const dataUrl = canvas.toDataURL('image/png');
    // setSignaturePreview(dataUrl);
    // onSignatureChange?.(dataUrl);
    // toast.success('Text signature created!');
  };

  // Show login message if not authenticated
  if (!user) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-8">
          <FileSignature className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <p className="text-gray-500">Please log in to manage your signature</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Digital Signature</h3>
        <p className="text-sm text-gray-500">
          Welcome, {user?.name || user?.email} • Upload or create your digital signature
        </p>
      </div>

      <div className="space-y-6">
        {/* Current Signature Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-gray-900">Current Signature</h4>
            {signaturePreview && (
              <button
                onClick={handleClearSignature}
                className="text-red-600 hover:text-red-700 text-sm font-medium flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Remove
              </button>
            )}
          </div>
          
          <div className="flex justify-center">
            <div className="relative bg-white border border-gray-300 rounded-lg p-4 max-w-md">
              <div className="aspect-[4/1.5] flex items-center justify-center">
                {signaturePreview ? (
                  <img 
                    src={signaturePreview} 
                    alt="Signature" 
                    className="max-h-32 max-w-full object-contain"
                  />
                ) : (
                  <div className="text-gray-400 text-center">
                    <FileSignature className="h-12 w-12 mx-auto mb-2" />
                    <p>No signature uploaded</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upload Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Option 1: Upload Image */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
            <div className="text-center">
              <div className="h-12 w-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Upload className="h-6 w-6" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Upload Image</h4>
              <p className="text-sm text-gray-500 mb-4">Upload PNG, JPG or SVG file</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                className="hidden"
                id="signature-upload"
              />
              <button
        onClick={() => {
          alert('Coming Soon..')
          toast.info('Coming Soon....')
        //  fileInputRef.current?.click()
        }}
                disabled={isUploading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {isUploading ? 'Uploading...' : 'Choose File'}
              </button>
            </div>
          </div>

          {/* Option 2: Draw Signature */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
            <div className="text-center">
              <div className="h-12 w-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Edit2 className="h-6 w-6" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Draw Signature</h4>
              <p className="text-sm text-gray-500 mb-4">Draw your signature with mouse</p>
              <button
                onClick={handleDrawSignature}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              >
                Start Drawing
              </button>
            </div>
          </div>

          {/* Option 3: Type Signature */}
          <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all">
            <div className="text-center">
              <div className="h-12 w-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileSignature className="h-6 w-6" />
              </div>
              <h4 className="font-medium text-gray-900 mb-2">Type Signature</h4>
              <p className="text-sm text-gray-500 mb-4">Use typed signature style</p>
              <div className="space-y-2">
                <input
                  type="text"
                  value={signatureText}
                  onChange={(e) => setSignatureText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Enter your name"
                />
                <button
                  onClick={handleTextSignature}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  Create Signature
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Drawing Canvas Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Draw Your Signature</h3>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 mb-4">Draw your signature in the box below</p>
                
                <div className="border-2 border-gray-300 rounded-lg mb-4">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={200}
                    className="w-full h-48 bg-white cursor-crosshair touch-none"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                  />
                </div>

                <div className="flex justify-between text-sm text-gray-500 mb-6">
                  <p>• Use mouse or touch to draw</p>
                  <p>• Clear and redraw if needed</p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      const canvas = canvasRef.current;
                      const ctx = canvas.getContext('2d');
                      ctx.fillStyle = '#ffffff';
                      ctx.fillRect(0, 0, canvas.width, canvas.height);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCanvasSignature}
                    disabled={isUploading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Signature
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Signature Guidelines */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Signature Guidelines</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Signature must match your legal name</li>
            <li>• Use clear, readable signature</li>
            <li>• Maximum file size: 2MB</li>
            <li>• Accepted formats: PNG, JPG, SVG</li>
            <li>• Your signature will be used for official documents</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SignatureUpload;